<template>
  <section class="q-features-page">
    <div class="container q-wrap">
      <!-- Header -->
      <div class="text-center q-head">
        <span class="q-pill">
          <i class="bi bi-grid-1x2-fill me-2"></i>Features
        </span>

        <h2 class="q-title mt-3">
          Everything you need to
          <span class="q-grad">study smarter</span>.
        </h2>

        <p class="q-sub mt-3 mx-auto">
          Turn notes into quizzes, flashcards, and a plan—then keep improving with progress tracking.
        </p>
      </div>

      <!-- Feature grid -->
      <div class="row g-4 mt-4">
        <div class="col-12 col-lg-4">
          <div class="q-card">
            <div class="q-ic"><i class="bi bi-camera"></i></div>
            <h3 class="q-h">Photo note analysis</h3>
            <p class="q-p">
              Upload a photo or PDF and instantly extract key concepts and topics.
            </p>
          </div>
        </div>

        <div class="col-12 col-lg-4">
          <div class="q-card">
            <div class="q-ic"><i class="bi bi-question-circle"></i></div>
            <h3 class="q-h">Auto quizzes</h3>
            <p class="q-p">
              Get multiple-choice and short-answer questions generated from your notes.
            </p>
          </div>
        </div>

        <div class="col-12 col-lg-4">
          <div class="q-card">
            <div class="q-ic"><i class="bi bi-stack"></i></div>
            <h3 class="q-h">Flashcards</h3>
            <p class="q-p">
              Key terms, definitions, formulas—organized and ready to drill.
            </p>
          </div>
        </div>

        <div class="col-12 col-lg-4">
          <div class="q-card">
            <div class="q-ic"><i class="bi bi-calendar2-check"></i></div>
            <h3 class="q-h">Personal study plan</h3>
            <p class="q-p">
              A simple schedule based on your deadline and the topics you need most.
            </p>
          </div>
        </div>

        <div class="col-12 col-lg-4">
          <div class="q-card">
            <div class="q-ic"><i class="bi bi-lightbulb"></i></div>
            <h3 class="q-h">Smart explanations</h3>
            <p class="q-p">
              Clear breakdowns with examples—great for tricky concepts and revision.
            </p>
          </div>
        </div>

        <div class="col-12 col-lg-4">
          <div class="q-card">
            <div class="q-ic"><i class="bi bi-graph-up-arrow"></i></div>
            <h3 class="q-h">Progress tracking</h3>
            <p class="q-p">
              See what you’re strong at, what needs work, and what to study next.
            </p>
          </div>
        </div>
      </div>

      <!-- Big highlight section -->
      <div class="row g-4 align-items-center mt-5">
        <div class="col-12 col-lg-6">
          <div class="q-big">
            <div class="q-big-top">
              <span class="q-mini"></span>
              <div>
                <div class="q-big-title">Generate a full study set</div>
                <div class="q-big-sub">Quizzes + flashcards + plan from one upload.</div>
              </div>
              <span class="q-badge ms-auto">Pro</span>
            </div>

            <div class="q-steps mt-4">
              <div class="q-step">
                <div class="q-step-n">1</div>
                <div>
                  <div class="q-step-t">Upload notes</div>
                  <div class="q-step-s">Photo, PDF, or screenshots.</div>
                </div>
              </div>

              <div class="q-step">
                <div class="q-step-n">2</div>
                <div>
                  <div class="q-step-t">AI extracts concepts</div>
                  <div class="q-step-s">Organizes topics & key terms.</div>
                </div>
              </div>

              <div class="q-step">
                <div class="q-step-n">3</div>
                <div>
                  <div class="q-step-t">You study & improve</div>
                  <div class="q-step-s">Practice, track, repeat.</div>
                </div>
              </div>
            </div>

            <div class="d-flex flex-wrap gap-3 mt-4">
              
              <RouterLink to="/notes" class="btn q-cta" >
                <i class="bi bi-camera me-2"></i>Analyze Notes
              </RouterLink>
              <RouterLink to="/pricing" class="btn q-secondary" >
                <i class="bi bi-currency-dollar me-2"></i>View Pricing
              </RouterLink>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-6">
          <div class="q-side">
            <div class="q-side-card">
              <div class="q-side-ic"><i class="bi bi-shield-lock"></i></div>
              <div>
                <div class="q-side-t">Privacy-first</div>
                <div class="q-side-s">Your notes stay yours. No weird sharing vibes.</div>
              </div>
            </div>

            <div class="q-side-card mt-3">
              <div class="q-side-ic"><i class="bi bi-lightning-charge"></i></div>
              <div>
                <div class="q-side-t">Fast workflow</div>
                <div class="q-side-s">From upload → study materials in seconds.</div>
              </div>
            </div>

            <div class="q-side-card mt-3">
              <div class="q-side-ic"><i class="bi bi-check2-circle"></i></div>
              <div>
                <div class="q-side-t">Clean & focused</div>
                <div class="q-side-s">No clutter. Just what helps you study.</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer CTA -->
      <div class="q-bottom-cta mt-5 text-center">
        <h3 class="q-bottom-title">Ready to turn notes into results?</h3>
        <p class="q-bottom-sub mx-auto">
          Upload your notes once and get quizzes, flashcards, and a plan that actually makes sense.
        </p>
        <div class="d-flex justify-content-center gap-3 flex-wrap mt-3">
          <RouterLink to="/notes" class="btn q-cta" >Analyze Notes</RouterLink>
          <RouterLink to="/" class="btn q-secondary" >Learn more</RouterLink>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default { name: "FeaturesPage" };
</script>

<style scoped>
.q-features-page {
  padding-top: 2.5rem;
  padding-bottom: 4.5rem;
}

.q-wrap {
  max-width: 72rem;
}

.q-head {
  max-width: 52rem;
  margin-left: auto;
  margin-right: auto;
}

.q-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.9rem;
  border-radius: 62.5rem;
  background: rgba(255, 255, 255, 0.06);
  border: 0.0625rem solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.85);
  font-weight: 800;
  font-size: 0.95rem;
}

.q-title {
  font-size: clamp(2rem, 3.2vw, 3rem);
  font-weight: 950;
  letter-spacing: -0.06rem;
  line-height: 1.05;
  color: rgba(255, 255, 255, 0.96);
}

.q-grad {
  background: linear-gradient(90deg, #2d6cff 0%, #7c3aed 55%, #ff3d8d 120%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.q-sub {
  font-size: 1.05rem;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.72);
  max-width: 90%;
}

/* cards */
.q-card {
  height: 100%;
  border-radius: 1.5rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.04);
  border: 0.0625rem solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 1.5rem 3rem rgba(0, 0, 0, 0.25);
  transition: transform 160ms ease, border-color 160ms ease, background 160ms ease;
}
.q-card:hover {
  transform: translateY(-0.125rem);
  background: rgba(255, 255, 255, 0.055);
  border-color: rgba(255, 255, 255, 0.12);
}

.q-ic {
  width: 3rem;
  height: 3rem;
  border-radius: 1.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border: 0.0625rem solid rgba(255, 255, 255, 0.10);
  margin-bottom: 1rem;
  color: rgba(255, 255, 255, 0.92);
  font-size: 1.2rem;
}

.q-h {
  font-weight: 950;
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
}

.q-p {
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.65;
  margin-bottom: 0;
}

/* big highlight */
.q-big {
  border-radius: 1.75rem;
  padding: 1.6rem;
  background: rgba(255, 255, 255, 0.04);
  border: 0.0625rem solid rgba(255, 255, 255, 0.10);
  box-shadow: 0 2rem 4.2rem rgba(0, 0, 0, 0.35);
}

.q-big-top {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.q-mini {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 1.1rem;
  background: linear-gradient(145deg, #2d6cff 0%, #7c3aed 55%, #ff3d8d 120%);
}

.q-big-title {
  font-weight: 950;
  color: rgba(255, 255, 255, 0.95);
}
.q-big-sub {
  color: rgba(255, 255, 255, 0.68);
  font-weight: 700;
  font-size: 0.95rem;
}

.q-badge {
  padding: 0.35rem 0.7rem;
  border-radius: 62.5rem;
  font-weight: 950;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.06);
  border: 0.0625rem solid rgba(255, 255, 255, 0.12);
}

/* steps */
.q-steps {
  display: grid;
  gap: 0.85rem;
}

.q-step {
  display: flex;
  gap: 0.9rem;
  padding: 1rem;
  border-radius: 1.25rem;
  background: rgba(0, 0, 0, 0.18);
  border: 0.0625rem solid rgba(255, 255, 255, 0.08);
}

.q-step-n {
  width: 2.2rem;
  height: 2.2rem;
  border-radius: 0.9rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 950;
  color: rgba(255, 255, 255, 0.92);
  background: rgba(255, 255, 255, 0.06);
  border: 0.0625rem solid rgba(255, 255, 255, 0.10);
}

.q-step-t {
  font-weight: 950;
  color: rgba(255, 255, 255, 0.93);
}
.q-step-s {
  color: rgba(255, 255, 255, 0.70);
  font-weight: 650;
  font-size: 0.95rem;
}

/* buttons */
.q-cta {
  border-radius: 62.5rem;
  padding: 0.75rem 1.25rem;
  font-weight: 950;
  color: #fff;
  border: 0;
  background: linear-gradient(90deg, #2d6cff 0%, #7c3aed 55%, #ff3d8d 120%);
  box-shadow: 0 1.125rem 2.25rem rgba(0, 0, 0, 0.35);
}
.q-cta:hover {
  filter: brightness(1.06);
  transform: translateY(-0.0625rem);
}

.q-secondary {
  border-radius: 62.5rem;
  padding: 0.75rem 1.25rem;
  font-weight: 900;
  color: rgba(255, 255, 255, 0.92);
  border: 0.0625rem solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.04);
}
.q-secondary:hover {
  background: rgba(255, 255, 255, 0.08);
}

/* side list */
.q-side {
  border-radius: 1.75rem;
  padding: 1.6rem;
  background: rgba(255, 255, 255, 0.03);
  border: 0.0625rem solid rgba(255, 255, 255, 0.08);
}

.q-side-card {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding: 1rem;
  border-radius: 1.25rem;
  background: rgba(255, 255, 255, 0.04);
  border: 0.0625rem solid rgba(255, 255, 255, 0.08);
}

.q-side-ic {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 1.1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border: 0.0625rem solid rgba(255, 255, 255, 0.10);
  color: rgba(255, 255, 255, 0.92);
  font-size: 1.15rem;
}

.q-side-t {
  font-weight: 950;
  color: rgba(255, 255, 255, 0.93);
}
.q-side-s {
  color: rgba(255, 255, 255, 0.70);
  line-height: 1.55;
}

/* bottom cta */
.q-bottom-cta {
  padding: 2rem 1rem;
  border-radius: 1.75rem;
  background: rgba(255, 255, 255, 0.04);
  border: 0.0625rem solid rgba(255, 255, 255, 0.08);
}
.q-bottom-title {
  font-weight: 950;
  color: rgba(255, 255, 255, 0.96);
  margin-bottom: 0.5rem;
}
.q-bottom-sub {
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.65;
  max-width: 44rem;
  margin-bottom: 0;
}

@media (max-width: 62rem) {
  .q-sub {
    max-width: 100%;
  }
}
</style>
